import {IAdditionalData, IExtensionOption} from "../../models/Extension";
import React, {useEffect, useState} from "react";
import {ComboBox, Icon, Label, TooltipHost} from "@fluentui/react";
import {useLazyGetExportExtensionOptionsQuery} from "../../store/Api";
import MarkdownView from "react-showdown";

interface IExtensionDependentOptionProps {
    initialValue: string;
    optionsField: IAdditionalData;
    parentFieldValue?: string;
    extensionId: string;
    onChange: (field: string, value: string) => void;
    onLoading?: (isLoading: boolean) => void;
    readOnly?: boolean;
    // Batch-options support: while `batchPending` is true this field waits for the form-level batch call instead
    // of fetching its own options. When the batch resolves, the form passes this field's options via
    // `preloadedOptions` (use them, skip the per-field fetch). If neither is set, it self-fetches as before —
    // preserving the original behavior for extensions without a batch URL and for dependent/search fields.
    batchPending?: boolean;
    preloadedOptions?: IExtensionOption[];
    // Options come from a live credentialed call to the extension. When the folio's credentials are known
    // to be failing, that call can only error — so the field doesn't attempt it and says why instead.
    suppressFetch?: boolean;
    // Mount-time auto-selection (re-announcing the initial value, or picking the declared default) is not
    // a user edit. Callers that must not treat it as one — e.g. forms that save on change — receive it
    // here instead; without this, it falls through to onChange, preserving the original behavior.
    onAutoSelect?: (field: string, value: string) => void;
}
const ExtensionDependentOption: React.FC<IExtensionDependentOptionProps> = (props) => {
    const [selectedOption, setSelectedOption] = useState<string>()
    const [options, setOptions] = useState<IExtensionOption[]>([])
    const [additionalData, setAdditionalData] = useState<{[p:string]: string} | undefined>(undefined)
    const [loadingOptions, setLoadingOptions] = useState<boolean>()
    // Whether the current `options` are a real, successfully fetched answer (as opposed to the initial
    // empty state, a pending batch, or a failed fetch). Staleness of the saved value can only be judged
    // against a real answer.
    const [optionsLoaded, setOptionsLoaded] = useState<boolean>(false)
    const [optionApi] = useLazyGetExportExtensionOptionsQuery();
    const [optionFetchError, setOptionFetchError] = useState<string | undefined>(undefined);
    useEffect(() => {
        if(props.optionsField.parentField && props.parentFieldValue){
            setAdditionalData({[props.optionsField.parentField]: props.parentFieldValue})
        }
    }, [props.parentFieldValue]);

    useEffect(() => {
        if (props.suppressFetch) {
            setOptionFetchError(undefined)
            setLoadingOptions(false)
            props.onLoading?.(false)
            return;
        }
        if (props.batchPending) {
            setOptionFetchError(undefined)
            setLoadingOptions(true)
            props.onLoading?.(true)
            return;
        }
        // The batch supplied this field's options — use them and skip the per-field fetch.
        if (props.preloadedOptions !== undefined) {
            setOptions(props.preloadedOptions)
            setOptionsLoaded(true)
            setOptionFetchError(undefined)
            setLoadingOptions(false)
            props.onLoading?.(false)
            return;
        }
        if(props.optionsField.field && props.optionsField.field != '' && props.extensionId) {
            if (props.optionsField.parentField && additionalData === undefined) return;
            setLoadingOptions(true)
            setOptionsLoaded(false)
            setOptionFetchError(undefined)
            props.onLoading?.(true)
            optionApi({
                extensionId: props.extensionId,
                field: props.optionsField.field,
                additionalData: additionalData
            }).then((res) => {
                if(res.data?.success) {
                    setOptions(res.data?.options ?? [])
                    setOptionsLoaded(true)
                }
                else{
                    setOptionFetchError(res.data?.error ?? "Failed to fetch options. Please verify your credentials and permissions.")
                }
                setLoadingOptions(false)


                props.onLoading?.(false)

            })
        }
    }, [additionalData, props.extensionId, props.optionsField.field, props.batchPending, props.preloadedOptions, props.suppressFetch])
    useEffect(() => {
        if(!selectedOption && options){
            const announce = props.onAutoSelect ?? props.onChange;
            if(props.initialValue){
                setSelectedOption(props.initialValue)
                announce(props.optionsField.field, props.initialValue)
            }
            else {
                const defaultOption = options?.filter((a) => a.default)
                if (defaultOption && defaultOption.length > 0) {
                    setSelectedOption(defaultOption[0].value)
                    announce(props.optionsField.field, defaultOption[0].value)
                }
            }
        }
    }, [options])

    const label = () => {
        if (props.optionsField.toolTip) {
            return (<TooltipHost content={<MarkdownView markdown={props.optionsField.toolTip}/>}>
                <div style={{display: "inline-flex"}}>
                    <Label required={!props.optionsField.optional}>
                        {props.optionsField.description}
                        <Icon iconName={'Info'} style={{paddingLeft: '5px'}}/>
                    </Label>
                </div>
            </TooltipHost>)
        }
        return (
            <div style={{display: "inline-flex"}}>
                <Label required={!props.optionsField.optional}>{props.optionsField.description}</Label>
            </div>)
    }
    
    // The saved value no longer exists among the options the extension just offered — the remote entity
    // was deleted or renamed. Left alone this breaks the extension quietly at run time, so it is called
    // out here, where the user can pick a replacement. Only judged against a real answer, never while
    // loading, after a failed fetch, or when lookups are suppressed.
    const savedValueUnavailable =
        optionsLoaded && !loadingOptions && !optionFetchError && !props.suppressFetch &&
        !!selectedOption && !options.some(o => o.value === selectedOption);

    const getError = () => {
        if(optionFetchError){
            return optionFetchError;
        }
        if (savedValueUnavailable) {
            return `The saved value "${selectedOption}" is no longer available from the connected system. Select a replacement.`;
        }
        return !props.optionsField.optional && !selectedOption? "Field is required" : ""
    }
    return(<React.Fragment>
            {label()}
            <ComboBox
                options={options?.map(c => ({
                    key: c.value,
                    text: c.display
                })) ?? []}
                errorMessage={getError()}
                autoComplete={"on"}
                disabled={!options || loadingOptions || props.readOnly || props.suppressFetch}
                required={!props.optionsField.optional}
                placeholder={props.suppressFetch ? "Validate credentials to load options" :
                    props.readOnly? `Select value for ${props.optionsField.parentField} to enable` : (loadingOptions ? "Loading Options..." : "Select Option")}
                selectedKey={selectedOption}
                onChange={(e, d) => {
                    setSelectedOption(d?.key?.toString() ?? "")
                    props.onChange(props.optionsField.field, d?.key.toString() ?? '')
            }}/>
        </React.Fragment>
        
        );
}
export default ExtensionDependentOption;